import React, { useMemo } from 'react';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import { usePsfShareAvailableShareActions } from '#src/app/pages/psf/share/shared/hooks';
import { useDeviceMessagingIcon } from '#src/app/shared/hooks/share';
import { useIsMobile } from '#src/app/shared/hooks/userAgent';

import { mapButtonConfigs, type ShareActionItem } from './mapButtonConfigs';

type UseActionItemsResult = ModelHookResult<{
	first: ShareActionItem | undefined;
	rest: readonly ShareActionItem[];
	mobileShareVisual: string | undefined;
}>;

export function useShareActions(): UseActionItemsResult {
	const pageData = usePsfSharePageData();
	const { bestShareVisual, shareAssetAb } = pageData.data.petition;
	const { isShareAssetsEnabled } = pageData.data.featureConfigs;
	const isMobile = useIsMobile();
	const { MessagingIconComponent, variant: smsVariant } = useDeviceMessagingIcon();

	const availableShareActions = usePsfShareAvailableShareActions();
	const buttonConfigs = useMemo(
		() =>
			mapButtonConfigs(availableShareActions, {
				sms: { icon: React.createElement(MessagingIconComponent), variant: smsVariant },
			}),
		[availableShareActions, MessagingIconComponent, smsVariant],
	);

	// EXPERIMENT: psf_share_asset_ab_experiment. A variant_1 sharer sees the image their links preview.
	const shareVisualUrl = shareAssetAb?.imageUrl ?? (isShareAssetsEnabled ? bestShareVisual?.url : undefined);
	const mobileShareVisual = isMobile ? shareVisualUrl : undefined;

	const [firstItem, ...restItems] = buttonConfigs;

	if (!firstItem) {
		return { data: { first: undefined, rest: [], mobileShareVisual } };
	}

	if (firstItem.channel !== 'copy') {
		return { data: { first: undefined, rest: buttonConfigs, mobileShareVisual } };
	}

	return { data: { first: firstItem, rest: restItems, mobileShareVisual } };
}
